/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { Search, MapPin, Star, Heart, ArrowLeft } from "lucide-react";
import { Pagination } from "../../components/client";
import FilterBar from "../../components/shared/FilterBar";
import { usePublicVendorServices } from "../../services/vendorService";
import useUserLocation from "../../hooks/useUserLocation";

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get search query and location from URL params
  const searchQuery = searchParams.get('q') || '';
  const locationParam = searchParams.get('location') || '';
  
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [location, setLocation] = useState(locationParam);
  const [priceMin, setPriceMin] = useState(1);
  const [priceMax, setPriceMax] = useState(1000);
  const [selectedPriceRange, setSelectedPriceRange] = useState("all");
  const [selectedRating, setSelectedRating] = useState("all");
  const [sortBy, setSortBy] = useState("none");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [hoveredService, setHoveredService] = useState(null);
  const [services, setServices] = useState([]);
  const [servicesPagination, setServicesPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 12,
    total: 0,
  });
  const [servicesCurrentPage, setServicesCurrentPage] = useState(1);

  // Auto-detect and set location on mount
  const { userLocation, loading: locationLoading } = useUserLocation();
  
  // Sync URL params with state
  useEffect(() => {
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
    if (locationParam) {
      setLocation(locationParam);
    }
  }, [searchQuery, locationParam]);

  // Auto-populate location when detected or updated
  useEffect(() => {
    if (userLocation && !location) {
      // Use address if available, otherwise use city/state
      const locationString = userLocation.address || 
                            (userLocation.city && userLocation.state ? `${userLocation.city}, ${userLocation.state}` : null) ||
                            userLocation.city || 
                            userLocation.state || 
                            null;
      
      // Always update location if we have a valid location string
      if (locationString) {
        setLocation(locationString);
        console.log('SearchResults: Location set to:', locationString);
      }
    }
  }, [userLocation, location]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setServicesCurrentPage(1);
  }, [
    searchTerm,
    location,
    priceMin,
    priceMax,
    sortBy,
    selectedRating,
    selectedPriceRange,
  ]);

  const sortOptions = [
    { value: "none", label: "No Sorting" },
    { value: "rating", label: "Highest Rated" },
    { value: "price_low", label: "Price: Low to High" },
    { value: "price_high", label: "Price: High to Low" },
    { value: "reviews", label: "Most Reviews" },
    { value: "distance", label: "Distance" },
  ];

  // Clear all filters function
  const clearAllFilters = () => {
    setSearchTerm("");
    setLocation("");
    setPriceMin(1);
    setPriceMax(1000);
    setSelectedPriceRange("all");
    setSelectedRating("all");
    setSortBy("none");
    setServicesCurrentPage(1);
    // Clear URL parameters
    navigate("/search", { replace: true });
  };

  // Build params for services
  const servicesParams = useMemo(() => {
    const baseParams = {
      ...(searchTerm && { search: searchTerm }),
      ...(priceMin && priceMin > 1 && { min_price: priceMin }),
      ...(priceMax && priceMax < 1000 && { max_price: priceMax }),
      ...(selectedRating && selectedRating !== "all" && { min_rating: parseFloat(selectedRating.replace('+', '')) }),
      per_page: 12,
      page: servicesCurrentPage,
    };
    
    // Add location-based filtering and sorting
    if (userLocation) {
      if (userLocation.latitude && userLocation.longitude) {
        baseParams.latitude = userLocation.latitude;
        baseParams.longitude = userLocation.longitude;
        if (userLocation.country) {
          baseParams.country = userLocation.country;
        }
        if (sortBy === "distance" || sortBy === "none") {
          baseParams.sort_by = "distance";
          baseParams.sort_order = "asc";
        }
      } else if (userLocation.city) {
        baseParams.city = userLocation.city;
      }
      if (!userLocation.latitude && !userLocation.longitude && userLocation.state) {
        baseParams.state = userLocation.state;
      }
      if (userLocation.country && !baseParams.country) {
        baseParams.country = userLocation.country;
      }
    } else if (location) {
      const locationParts = location.split(', ');
      if (locationParts.length >= 2) {
        baseParams.city = locationParts[0];
        baseParams.state = locationParts[1];
      } else {
        baseParams.city = location;
      }
      if (userLocation?.country) {
        baseParams.country = userLocation.country;
      }
    }
    
    if (userLocation?.country && !baseParams.country) {
      baseParams.country = userLocation.country;
    }
    
    // Handle other sort options
    if (sortBy !== "none" && sortBy !== "distance" && !baseParams.sort_by) {
      baseParams.sort_by =
        sortBy === "rating"
          ? "rating"
          : sortBy === "price_low" || sortBy === "price_high"
          ? "price"
          : "created_at";
      baseParams.sort_order = sortBy === "price_low" ? "asc" : "desc";
    }
    
    return baseParams;
  }, [searchTerm, priceMin, priceMax, selectedRating, servicesCurrentPage, userLocation, location, sortBy]);

  // Only fetch when we have country information to filter by location
  const hasCountryFilter = !!(servicesParams.country);
  const shouldFetch = hasCountryFilter && !locationLoading;

  // Fetch services
  const { data: servicesResp, isLoading: isServicesLoading } = usePublicVendorServices(
    servicesParams,
    {
      enabled: shouldFetch,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      keepPreviousData: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  // Process services data
  useEffect(() => {
    if (isServicesLoading) {
      return;
    }
    
    let servicesData = [];
    if (servicesResp?.success !== false) {
      if (servicesResp?.data?.data && Array.isArray(servicesResp.data.data)) {
        servicesData = servicesResp.data.data;
      } else if (Array.isArray(servicesResp?.data)) {
        servicesData = servicesResp.data;
      } else if (Array.isArray(servicesResp)) {
        servicesData = servicesResp;
      }
    }
    
    const paginationData = servicesResp?.data || {};
    setServicesPagination({
      current_page: paginationData.current_page || 1,
      last_page: paginationData.last_page || 1,
      per_page: paginationData.per_page || 12,
      total: paginationData.total || 0,
    });
    
    // Transform services data
    const transformed = servicesData.map((service) => {
      const vendorProfile = service.vendor_profile || service.vendorProfile;
      const vendorUser = vendorProfile?.user || vendorProfile?.User;
      const city = vendorProfile?.city || '';
      const state = vendorProfile?.state || '';
      const locationStr = city && state ? `${city}, ${state}` : city || state || 'Location not available';
      
      return {
        id: service.id,
        title: service.service_name || service.serviceName || 'Service',
        rating: service.average_rating || service.averageRating || vendorProfile?.average_rating || 0,
        price: parseFloat(service.base_price || service.basePrice || 0),
        image: service.image_url || 
               service.imageUrl ||
               vendorProfile?.image_url || 
               vendorProfile?.imageUrl ||
               vendorUser?.profile_image_url ||
               vendorUser?.profileImageUrl ||
               'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=400&h=300&fit=crop',
        location: locationStr,
        isFeatured: service.is_featured || service.isFeatured || false,
        vendor: vendorProfile?.business_name || vendorProfile?.businessName || 'Vendor',
        reviewCount: service.total_reviews || service.totalReviews || vendorProfile?.total_reviews || 0
      };
    });
    
    setServices(transformed);
  }, [servicesResp, isServicesLoading]);

  // Services are already filtered by the API, but we can do client-side filtering if needed
  const filteredServices = services;

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile: Title Section */}
      <div className="bg-white border-b border-gray-200 md:hidden">
        <div className="page-container py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-black transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="text-sm">Back</span>
            </Link>
            <h1 className="text-lg font-bold text-gray-900 text-center flex-1 mx-4">
              Search Results
            </h1>
            <div className="w-12"></div>
          </div>
        </div>
      </div>
              
      {/* Mobile: Sticky Filter Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm md:hidden">
        <div className="page-container py-3">
          <FilterBar
            selectedPriceRange={selectedPriceRange}
            setSelectedPriceRange={setSelectedPriceRange}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            showMoreFilters={showMoreFilters}
            setShowMoreFilters={setShowMoreFilters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            location={location}
            setLocation={setLocation}
            selectedCategory=""
            setSelectedCategory={() => {}}
            categories={[]}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOptions={sortOptions}
            clearAllFilters={clearAllFilters}
            showCategoryFilter={false}
            priceMin={priceMin}
            setPriceMin={setPriceMin}
            priceMax={priceMax}
            setPriceMax={setPriceMax}
          />
        </div>
      </div>

      {/* Desktop: One Line Header - Back | Filters | Count (Sticky) */}
      <div className="hidden md:block bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="page-container py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Back Button */}
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-black transition-colors whitespace-nowrap"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span className="text-base">Back</span>
            </Link>

            <div className="flex-1"></div>

            {/* Filter Bar on Right */}
            <div className="flex items-center gap-4">
              <FilterBar
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                selectedRating={selectedRating}
                setSelectedRating={setSelectedRating}
                showMoreFilters={showMoreFilters}
                setShowMoreFilters={setShowMoreFilters}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                location={location}
                setLocation={setLocation}
                selectedCategory=""
                setSelectedCategory={() => {}}
                categories={[]}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortOptions={sortOptions}
                clearAllFilters={clearAllFilters}
                showCategoryFilter={false}
                priceMin={priceMin}
                setPriceMin={setPriceMin}
                priceMax={priceMax}
                setPriceMax={setPriceMax}
              />

              {/* Results Count */}
              <p className="text-sm text-gray-600 whitespace-nowrap">
                {(isServicesLoading || locationLoading) ? "Loading..." : `${filteredServices.length} services`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="page-container py-4 md:py-8 md:px-8 px-8">
        {/* Mobile Results Count */}
        <p className="text-sm text-gray-600 mb-4 md:hidden">
          {(isServicesLoading || locationLoading) ? "Loading..." : `${filteredServices.length} services found`}
        </p>

        {/* Desktop: Title next to cards */}
        <div className="hidden md:block mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            {searchTerm ? `Search Results for "${searchTerm}"` : 'Search Results'}
          </h2>
          {location && (
            <p className="text-sm text-gray-600">in {location}</p>
          )}
        </div>

        <div className="flex gap-8 relative">
          {/* Main Content Area */}
          <div className="flex-1">
            {/* Loading State */}
            {(isServicesLoading || locationLoading) ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-4">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div
                    key={i}
                    className="bg-white rounded-xl md:rounded-2xl overflow-hidden border border-gray-200 animate-pulse"
                  >
                    <div className="h-52 bg-gray-200"></div>
                    <div className="p-3 md:p-4">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-3"></div>
                      <div className="h-3 bg-gray-200 rounded mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Display Services */}
                {filteredServices.length > 0 ? (
                  <div className="mb-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-4">
                      {filteredServices.map((service) => (
                        <div
                          key={service.id}
                          onMouseEnter={() => setHoveredService(service)}
                          onMouseLeave={() => setHoveredService(null)}
                          className="relative"
                        >
                          <Link
                            to={`/services/${service.id}`}
                            className="block group bg-white rounded-xl md:rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-200 md:border-transparent md:hover:border-gray-200"
                          >
                            {/* Image */}
                            <div className="relative h-52 md:h-52 overflow-hidden">
                              <img
                                src={service.image}
                                alt={service.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />

                              {/* Featured Badge */}
                              {service.isFeatured && (
                                <div className="absolute top-2 md:top-3 left-2 md:left-3 bg-black text-white px-2 md:px-3 py-1 rounded-full text-xs font-medium">
                                  Featured
                                </div>
                              )}

                              {/* Heart Icon */}
                              {/* <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                className="absolute top-2 md:top-3 right-2 md:right-3 p-1.5 md:p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors md:opacity-0 md:group-hover:opacity-100"
                                aria-label="Add to favorites"
                              >
                                <Heart className="w-4 h-4 text-gray-700" />
                              </button> */}
                            </div>

                            {/* Content */}
                            <div className="p-3 md:p-4">
                              <div className="flex items-start justify-between mb-2">
                                <h3 className="text-sm md:text-sm font-semibold text-gray-900 line-clamp-2 flex-1">
                                  {service.title}
                                </h3>
                                <div className="flex items-center ml-2 flex-shrink-0">
                                  <Star className="w-3 h-3 fill-current text-gray-900" />
                                  <span className="text-xs font-medium text-gray-900 ml-1">
                                    {service.rating > 0 ? Number(service.rating).toFixed(2) : 'New'}
                                  </span>
                                </div>
                              </div>

                              <p className="text-xs text-gray-500 mb-1">
                                {service.vendor}
                              </p>

                              <div className="flex items-center text-xs text-gray-500 mb-2 md:mb-3">
                                <MapPin className="w-3 h-3 mr-1" />
                                {service.location}
                              </div>

                              <div className="flex items-center justify-between pt-2 md:pt-3 border-t border-gray-100">
                                <div>
                                  <span className="text-sm font-bold text-gray-900">
                                    ${service.price > 0 ? service.price.toFixed(0) : 'Contact'}
                                  </span>
                                  <span className="text-xs text-gray-600 ml-1">
                                    per event
                                  </span>
                                </div>
                                {service.reviewCount > 0 && (
                                  <span className="text-xs text-gray-500 hidden sm:inline">
                                    {service.reviewCount} reviews
                                  </span>
                                )}
                              </div>
                            </div>
                          </Link>
                        </div>
                      ))}
                    </div>
                    {/* Pagination for Services */}
                    {!locationLoading && filteredServices.length > 0 && (
                      <Pagination
                        currentPage={servicesPagination.current_page}
                        lastPage={servicesPagination.last_page}
                        perPage={servicesPagination.per_page}
                        total={servicesPagination.total}
                        onPageChange={setServicesCurrentPage}
                      />
                    )}
                  </div>
                ) : (
                  /* No Results - Only show when location detection and data fetching are complete */
                  !locationLoading && !isServicesLoading && (
                    <div className="text-center py-12">
                      <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="w-12 h-12 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No services found
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {searchTerm 
                          ? `No services found for "${searchTerm}"${location ? ` in ${location}` : ''}`
                          : 'Try adjusting your search criteria or filters'
                        }
                      </p>
                      <button
                        onClick={clearAllFilters}
                        className="px-4 py-2 bg-black text-white rounded-xl font-light hover:bg-gray-800 transition-colors"
                      >
                        Clear Filters
                      </button>
                    </div>
                  )
                )}

                {/* Side Detail Panel for Services - Shows on Hover (Desktop Only) */}
                {hoveredService && (
                  <div className="hidden xl:block fixed right-8 top-1/4 w-96 z-30">
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 animate-fade-in">
                      <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                        <img
                          src={hoveredService.image}
                          alt={hoveredService.title}
                          className="w-full h-full object-cover"
                        />
                        {hoveredService.isFeatured && (
                          <div className="absolute top-3 left-3 bg-black text-white px-3 py-1 rounded-full text-xs font-medium">
                            Featured
                          </div>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {hoveredService.title}
                      </h3>

                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center">
                          <Star className="w-4 h-4 fill-current text-gray-900 mr-1" />
                          <span className="font-semibold">
                            {hoveredService.rating > 0 ? Number(hoveredService.rating).toFixed(2) : 'New'}
                          </span>
                          {hoveredService.reviewCount > 0 && (
                            <span className="text-gray-500 text-sm ml-1">
                              ({hoveredService.reviewCount} reviews)
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center text-gray-600 mb-4">
                        <MapPin className="w-4 h-4 mr-2" />
                        {hoveredService.location}
                      </div>

                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Provided by:</p>
                        <p className="font-semibold text-gray-900">
                          {hoveredService.vendor}
                        </p>
                      </div>

                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-baseline justify-between mb-4">
                          <div>
                            <span className="text-2xl font-bold text-gray-900">
                              ${hoveredService.price > 0 ? hoveredService.price.toFixed(0) : 'Contact'}
                            </span>
                            {hoveredService.price > 0 && (
                              <span className="text-gray-600 text-sm ml-2">
                                per event
                              </span>
                            )}
                          </div>
                        </div>

                        <Link
                          to={`/services/${hoveredService.id}`}
                          className="block w-full py-3 bg-black text-white text-center rounded-xl font-light hover:bg-gray-800 transition-colors"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;

